import { useEffect, useState } from 'react';

export type RefreshIntervalSeconds = 5 | 10 | 30 | 60;
export type TableDensity = 'comfortable' | 'compact';
export type DashboardRowCount = 5 | 10 | 15;

export interface DashboardControlsState {
  autoRefresh: boolean;
  refreshIntervalSeconds: RefreshIntervalSeconds;
  tableDensity: TableDensity;
  dashboardRowCount: DashboardRowCount;
}

const storageKey = 'oddsquake.dashboardControls.v1';

export const defaultDashboardControls: DashboardControlsState = {
  autoRefresh: true,
  refreshIntervalSeconds: 10,
  tableDensity: 'comfortable',
  dashboardRowCount: 10,
};

export function useDashboardControls() {
  const [controls, setControls] = useState<DashboardControlsState>(() => readStoredControls());

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(controls));
  }, [controls]);

  function resetControls() {
    setControls(defaultDashboardControls);
  }

  return { controls, setControls, resetControls };
}

function readStoredControls(): DashboardControlsState {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return defaultDashboardControls;
    const parsed = JSON.parse(raw) as Partial<DashboardControlsState>;
    return {
      autoRefresh: typeof parsed.autoRefresh === 'boolean' ? parsed.autoRefresh : defaultDashboardControls.autoRefresh,
      refreshIntervalSeconds: isRefreshInterval(parsed.refreshIntervalSeconds)
        ? parsed.refreshIntervalSeconds
        : defaultDashboardControls.refreshIntervalSeconds,
      tableDensity: parsed.tableDensity === 'compact' ? 'compact' : defaultDashboardControls.tableDensity,
      dashboardRowCount: isDashboardRowCount(parsed.dashboardRowCount)
        ? parsed.dashboardRowCount
        : defaultDashboardControls.dashboardRowCount,
    };
  } catch {
    return defaultDashboardControls;
  }
}

function isRefreshInterval(value: unknown): value is RefreshIntervalSeconds {
  return value === 5 || value === 10 || value === 30 || value === 60;
}

function isDashboardRowCount(value: unknown): value is DashboardRowCount {
  return value === 5 || value === 10 || value === 15;
}
